import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { staffService } from '../../services';
import { Loader } from '../../components';
import { ArrowLeft, Edit2 } from 'lucide-react';
import StaffDetails from './StaffDetails';

/**
 * Staff View — the full-page staff record.
 *
 * The Staff list opens this record in a slide-over now (see StaffList.js), so
 * this page is the addressable version of the same thing: a link somebody
 * bookmarked, and where Recruitment lands a candidate after transferring them
 * into the directory. Both render StaffDetails, so there is one implementation
 * of the record and no chance of the two drifting.
 */
const StaffView = () => {
  const navigate = useNavigate();
  const { staffId } = useParams();
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await staffService.getStaffById(staffId);
      if (res.success) setStaff(res.data);
      else setError(res.message || 'Failed to load staff member');
    } catch (err) {
      setError(err.message || 'Error loading staff member');
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Loader text="Loading staff member..." />
      </div>
    );
  }

  if (error || !staff) {
    return (
      <div className="p-12 text-center">
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/50 text-red-600 dark:text-red-400 px-6 py-4 rounded-xl inline-block">
          {error || 'Staff member not found'}
        </div>
        <button onClick={() => navigate('/manager/staffs')} className="block mx-auto mt-4 text-sm text-blue-600 font-medium hover:underline">
          Back to Staff
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1100px] mx-auto space-y-6 pb-12">
      {/* Back + Edit */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/manager/staffs')}
          className="inline-flex items-center gap-2 text-[14px] font-medium text-[#64748B] dark:text-gray-400 hover:text-[#1E293B] dark:hover:text-gray-200 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Staff
        </button>
        <button
          onClick={() => navigate(`/manager/staffs/${staffId}/edit`)}
          className="inline-flex items-center gap-2 bg-[#2563EB] text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition text-[13px] font-semibold shadow-sm"
        >
          <Edit2 className="w-4 h-4" /> Edit Staff
        </button>
      </div>

      <StaffDetails staff={staff} />
    </div>
  );
};

export default StaffView;
